import type { Column } from '@talos/kyoko';
import { getColumnDynamicsState } from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import type { MarketSelectorValue } from '../../MarketSelector/types';
import type { PriceGridEntity } from '../PriceGridEntity';
import type { PriceGridSecurityMarketDataColumnParams } from './types';

export const PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX = 'price';
export const PRICE_GRID_MARKET_DATA_BID_QTY_COLUMN_SUFFIX = 'bid-qty';
export const PRICE_GRID_MARKET_DATA_OFFER_QTY_COLUMN_SUFFIX = 'offer-qty';
export const PRICE_GRID_MARKET_DATA_RFQ_COLUMN_SUFFIX = 'rfq';
export const PRICE_GRID_MARKET_DATA_LEG_MARKET_SELECTOR_COLUMN_SUFFIX = 'leg-market-selector';
export const PRICE_GRID_MARKET_DATA_MARKET_STATUS_COLUMN_SUFFIX = 'market-status';
export const PRICE_GRID_MARKET_DATA_MENU_COLUMN_SUFFIX = 'menu';
export const PRICE_GRID_MARKET_DATA_CHILD_HEADER_CLASS = 'price-grid-market-data-child-header';

export const PRICE_GRID_RFQ_COLUMN_WIDTH = 48;
export const PRICE_GRID_SYMBOL_MENU_COLUMN_WIDTH = 40;

/** Shared props applied to every leaf column inside a market data column group.
 *  Hides the child header row while preserving the native group header. */
export const PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS = {
  headerClass: PRICE_GRID_MARKET_DATA_CHILD_HEADER_CLASS,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true,
} as const;

export type PriceGridMarketDataRendererParams<TValue = PriceGridEntity> = ICellRendererParams<PriceGridEntity, TValue>;
export type PriceGridMarketDataColumnRow = PriceGridEntity & Record<string, unknown>;

export interface PriceGridMarketDataActionColumnParams {
  priceGridMarketSelectorValue?: MarketSelectorValue;
}

export function getPriceGridMarketDataColumnTitle(
  column: Pick<Column<unknown, PriceGridMarketDataColumnRow>, 'dynamics' | 'title'>,
  fallback = 'Price'
) {
  const dynamicsTitle = getColumnDynamicsState<{ title?: string }>(column.dynamics)?.title;
  if (dynamicsTitle) {
    return dynamicsTitle;
  }
  return typeof column.title === 'string' && column.title.length > 0 ? column.title : fallback;
}

export function getPriceGridMarketDataColumnGroupId(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>
) {
  return column.id ?? column.dynamics?.colID ?? column.field ?? 'price-grid-market-data';
}

export function getPriceGridMarketDataColumnId(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
  suffix: string
) {
  return `${getPriceGridMarketDataColumnGroupId(column)}-${suffix}`;
}

export function getSymbolFromRow(
  column: Pick<Column<unknown, PriceGridMarketDataColumnRow>, 'field'>,
  row: PriceGridEntity
) {
  const symbolField = column.field;
  const symbol = symbolField ? get(row, symbolField) : undefined;
  return typeof symbol === 'string' && symbol.length > 0 ? symbol : undefined;
}

export function getLegIndexFromPriceGridMarketDataColumnField(field: string | undefined) {
  const match = field?.match(/^security\.MultilegDetails\.Legs\[(\d+)\]\.Symbol$/);
  return match ? Number(match[1]) : undefined;
}

export function getPriceGridMarketSelectorValue(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>
): MarketSelectorValue | undefined {
  return getColumnDynamicsState(column.dynamics)?.marketSelectorValue;
}
